import { useState } from "react";
import { format, parseISO } from "date-fns";
import { id as idLocale, enUS } from "date-fns/locale";
import { CalendarIcon, X } from "lucide-react";
import { useLang } from "../i18n/LangContext";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Label } from "./ui/label";

export function formatDateID(iso, lang = "id") {
  if (!iso) return "";
  try {
    return format(parseISO(iso.slice(0, 10)), "d MMM yyyy", { locale: lang === "en" ? enUS : idLocale });
  } catch {
    return iso;
  }
}

/** Indonesian calendar picker replacing the browser-native date input. */
export function DateField({ label, testId, value, onChange, placeholder, clearable = true, required, disabled }) {
  const { lang, t } = useLang();
  const [open, setOpen] = useState(false);
  const locale = lang === "en" ? enUS : idLocale;
  const selected = value ? parseISO(value.slice(0, 10)) : undefined;

  return (
    <div className="space-y-1.5">
      {label && <Label className="text-slate-300 text-sm">{label}</Label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" data-testid={testId} disabled={disabled}
            className="w-full h-10 rounded-md bg-[#1C202E] border border-white/10 px-3 text-left text-sm text-white inline-flex items-center gap-2 transition hover:border-white/25 disabled:opacity-50">
            <CalendarIcon className="h-4 w-4 text-slate-400 shrink-0" />
            <span className={value ? "" : "text-slate-500"}>
              {value ? formatDateID(value, lang) : (placeholder || t("common.selectDate"))}
            </span>
            {clearable && value && !disabled && (
              <span role="button" tabIndex={-1} data-testid={testId ? `${testId}-clear` : undefined}
                onClick={(e) => { e.stopPropagation(); onChange(""); }}
                className="ml-auto text-slate-500 hover:text-white"><X className="h-3.5 w-3.5" /></span>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-0 bg-[#10121B] border-white/10">
          <Calendar mode="single" locale={locale} selected={selected} defaultMonth={selected}
            onSelect={(d) => { onChange(d ? format(d, "yyyy-MM-dd") : ""); setOpen(false); }}
            className="text-white [&_.rdp-day_selected]:bg-[#FF3B30] [&_.rdp-day_selected]:text-white" />
        </PopoverContent>
      </Popover>
      {required && !value && <input tabIndex={-1} required value="" onChange={() => {}} className="sr-only h-0 w-0" />}
    </div>
  );
}
